import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Download, 
  Filter, 
  Settings, 
  ChevronDown, 
  Eye, 
  Edit, 
  Trash2,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  X,
  Save,
  User,
  Shield,
  Key
} from 'lucide-react';
import userService from '../../services/userService';
import { toast } from 'react-toastify';
import UserExportModal from '../UserExportModal';
import UserConfigModal from '../UserConfigModal';
import { useLanguage } from '../../contexts/LanguageContext';
import './UsersTable.scss';

const UsersTable = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchDoterraId, setSearchDoterraId] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  // Configuration states
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('users-visible-columns');
    return saved ? JSON.parse(saved) : {
      id: true,
      email: true,
      name: true,
      phone: true,
      doterraId: true,
      mentor: true,
      status: true,
      createdAt: true,
      actions: true
    };
  });

  // User details/edit modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit'
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [showDoterraForm, setShowDoterraForm] = useState(false);

  // Table settings
  const [tableSettings, setTableSettings] = useState(() => {
    const saved = localStorage.getItem('users-table-settings');
    return saved ? JSON.parse(saved) : {
      rowsPerPage: 10,
      compactView: false,
      showAdvancedInfo: true
    };
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.dropdown')) {
        setShowActionsDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionsDropdown]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('users-visible-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('users-table-settings', JSON.stringify(tableSettings));
  }, [tableSettings]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      
      // Handle different response formats
      let usersData = [];
      if (Array.isArray(response)) {
        usersData = response;
      } else if (response && Array.isArray(response.users)) {
        usersData = response.users;
      } else if (response && typeof response === 'object') {
        usersData = [response];
      }
      
      setUsers(usersData);
      console.log('Users loaded:', usersData.length);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('users.loadError'));
      setUsers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Ești sigur că vrei să ștergi acest utilizator?')) {
      try {
        await userService.deleteUser(userId);
        toast.success('Utilizator șters cu succes');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Eroare la ștergerea utilizatorului');
      }
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setModalMode('view');
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditingUser({ ...user });
    setModalMode('edit');
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    // Validation
    if (!editingUser?.firstName?.trim()) {
      toast.error('Numele este obligatoriu');
      return;
    }
    
    if (!editingUser?.lastName?.trim()) {
      toast.error('Prenumele este obligatoriu');
      return;
    }
    
    if (!editingUser?.email?.trim()) {
      toast.error('Email-ul este obligatoriu');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingUser.email)) {
      toast.error('Adresa de email nu este validă');
      return;
    }
    
    // Phone validation (if provided)
    if (editingUser?.phoneNumber && editingUser.phoneNumber.trim()) {
      const phoneRegex = /^[+]?[0-9\s\-()]{8,}$/;
      if (!phoneRegex.test(editingUser.phoneNumber.replace(/\s/g, ''))) {
        toast.error('Numărul de telefon nu este valid');
        return;
      }
    }

    setSavingUser(true);
    try {
      // Prepare data for update
      const updateData = {
        firstName: editingUser.firstName.trim(),
        lastName: editingUser.lastName.trim(),
        email: editingUser.email.trim().toLowerCase(),
        phoneNumber: editingUser.phoneNumber?.trim() || '',
        hasDoterraAccount: editingUser.hasDoterraAccount || 'no',
        acceptTerms: editingUser.acceptTerms || false
      };

      // Add doTERRA fields if applicable
      if (editingUser.hasDoterraAccount === 'yes') {
        updateData.doterraFullName = editingUser.doterraFullName?.trim() || '';
        updateData.doterraId = editingUser.doterraId?.trim() || '';
        updateData.doterraEmail = editingUser.doterraEmail?.trim() || '';
        updateData.doterraPhone = editingUser.doterraPhone?.trim() || '';
        updateData.mentorFullName = editingUser.mentorFullName?.trim() || '';
        updateData.mentorDoterraId = editingUser.mentorDoterraId?.trim() || '';
      }

      await userService.updateUser(editingUser.docId, updateData);
      toast.success('Utilizator actualizat cu succes');
      setShowUserModal(false);
      setModalMode('view');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Eroare la actualizarea utilizatorului');
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await userService.updateUser(userId, { status: newStatus });
      toast.success(`Status utilizator ${newStatus === 'active' ? 'activat' : 'dezactivat'} cu succes`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Eroare la actualizarea statusului');
    }
  };

  // Note: Role change functionality available but not currently used in UI
  // const handleChangeUserRole = async (userId, newRole) => {
  //   try {
  //     await userService.updateUser(userId, { role: newRole });
  //     toast.success(`Rol utilizator schimbat în ${newRole} cu succes`);
  //     fetchUsers();
  //   } catch (error) {
  //     console.error('Error updating user role:', error);
  //     toast.error('Eroare la actualizarea rolului');
  //   }
  // };

  const handleSendEmail = (userEmail) => {
    if (window.confirm(`Dorești să trimiți un email la ${userEmail}?`)) {
      window.location.href = `mailto:${userEmail}`;
    }
  };

  const handleCallUser = (phoneNumber) => {
    if (phoneNumber && window.confirm(`Dorești să apelezi ${phoneNumber}?`)) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleBulkAction = async (action) => {
    const selectedUserObjs = users.filter(user => selectedUsers.includes(user.docId));
    
    if (selectedUserObjs.length === 0 && action !== 'export') {
      toast.warning('Te rugăm să selectezi cel puțin un utilizator');
      return;
    }

    switch (action) {
      case 'activate':
        try {
          for (const user of selectedUserObjs) {
            await userService.updateUser(user.docId, { status: 'active' });
          }
          toast.success(`${selectedUserObjs.length} utilizatori activați cu succes`);
          fetchUsers();
          setSelectedUsers([]);
        } catch (error) {
          console.error('Error activating users:', error);
          toast.error('Eroare la activarea utilizatorilor');
        }
        break;
        
      case 'deactivate':
        try {
          for (const user of selectedUsers) {
            await userService.updateUser(user.docId, { status: 'inactive' });
          }
          toast.success(`${selectedUsers.length} utilizatori dezactivați cu succes`);
          fetchUsers();
        } catch (error) {
          console.error('Error deactivating users:', error);
          toast.error('Eroare la dezactivarea utilizatorilor');
        }
        break;
        
      case 'sendEmail':
        try {
          const validUsers = selectedUsers.filter(user => user.email);
          if (validUsers.length === 0) {
            toast.warning('Niciun utilizator selectat nu are email valid');
            return;
          }

          // Show email options modal
          const emailType = prompt(`Selectează tipul de email pentru ${validUsers.length} utilizatori:
1. Mesaj personalizat
2. Email de bun venit
3. Notificare importantă
4. Ofertă promoțională

Introdu numărul opțiunii (1-4):`);

          if (!emailType || !['1', '2', '3', '4'].includes(emailType)) {
            return;
          }

          switch (emailType) {
            case '1': // Custom message
              const customSubject = prompt('Introdu subiectul emailului:');
              if (!customSubject) return;
              
              const customContent = prompt('Introdu conținutul emailului (HTML acceptat):');
              if (!customContent) return;
              
              await userService.sendCustomEmail(validUsers, customSubject, customContent);
              break;

            case '2': // Welcome email
              await userService.sendBulkEmail(validUsers, 'Bun venit la theOilsBabe!', {}, 'welcome');
              break;

            case '3': // Notification
              const notificationSubject = prompt('Introdu subiectul notificării:');
              if (!notificationSubject) return;
              
              const notificationText = prompt('Introdu textul notificării:');
              if (!notificationText) return;
              
              const notificationDetails = prompt('Detalii suplimentare (opțional):') || '';
              
              await userService.sendNotificationEmail(validUsers, {
                subject: notificationSubject,
                text: notificationText,
                details: notificationDetails,
                actionUrl: 'https://theoilsbabe.com/dashboard'
              });
              break;

            case '4': // Promotion
              const promotionSubject = prompt('Introdu subiectul ofertei:') || 'Ofertă specială pentru tine!';
              const promotionText = prompt('Introdu textul promoției:');
              if (!promotionText) return;
              
              const promotionUrl = prompt('URL pentru ofertă (opțional):') || 'https://theoilsbabe.com/oferta';
              const expirationDate = prompt('Data expirării (opțional):') || 'sfârșitul săptămânii';
              
              await userService.sendPromotionEmail(validUsers, {
                subject: promotionSubject,
                text: promotionText,
                url: promotionUrl,
                expirationDate: expirationDate
              });
              break;

            default:
              toast.warning('Opțiune invalidă selectată');
              return;
          }

          toast.success(`Email trimis cu succes către ${validUsers.length} utilizatori!`);
        } catch (error) {
          console.error('Error sending emails:', error);
          toast.error('Eroare la trimiterea emailurilor: ' + error.message);
        }
        break;
        
      case 'changeRole':
        try {
          const newRole = prompt('Introdu noul rol (admin, user, moderator):');
          if (!newRole) return;
          
          for (const user of selectedUsers) {
            await userService.updateUser(user.docId, { role: newRole });
          }
          toast.success(`Rol schimbat pentru ${selectedUsers.length} utilizatori`);
          fetchUsers();
        } catch (error) {
          console.error('Error changing user roles:', error);
          toast.error('Eroare la schimbarea rolurilor');
        }
        break;
        
      case 'export':
        handleExport();
        break;
        
      case 'delete':
        if (window.confirm(`Ești sigur că vrei să ștergi ${selectedUsers.length} utilizatori selectați? Această acțiune nu poate fi anulată.`)) {
          try {
            for (const user of selectedUsers) {
              await userService.deleteUser(user.docId);
            }
            toast.success(`${selectedUsers.length} utilizatori șterși cu succes`);
            fetchUsers();
          } catch (error) {
            console.error('Error deleting users:', error);
            toast.error('Eroare la ștergerea utilizatorilor');
          }
        }
        break;
        
      default:
        break;
    }
    setShowActionsDropdown(false);
  };

  const handleDeleteAll = async () => {
    if (window.confirm('Ești sigur că vrei să ștergi TOȚI utilizatorii? Această acțiune nu poate fi anulată și va șterge întreaga bază de date!')) {
      if (window.confirm('ULTIMA CONFIRMARE: Aceasta va șterge TOȚI utilizatorii din sistem. Continui?')) {
        try {
          await userService.deleteAllUsers();
          toast.success('Toți utilizatorii au fost șterși cu succes');
          fetchUsers();
        } catch (error) {
          console.error('Error deleting all users:', error);
          toast.error('Eroare la ștergerea utilizatorilor');
        }
      }
    }
  };

  // Configuration handlers
  const handleColumnVisibilityChange = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleTableSettingsChange = (setting, value) => {
    setTableSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const resetToDefaults = () => {
    setVisibleColumns({
      id: true,
      email: true,
      name: true,
      phone: true,
      doterraId: true,
      mentor: true,
      status: true,
      createdAt: true,
      actions: true
    });
    setTableSettings({
      rowsPerPage: 10,
      compactView: false,
      showAdvancedInfo: true
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phoneNumber?.includes(searchTerm) ||
      user.doterraId?.includes(searchTerm) ||
      user.mentorDoterraId?.includes(searchTerm);

    const matchesEmailSearch = !searchEmail || 
      user.email?.toLowerCase().includes(searchEmail.toLowerCase());

    const matchesDoterraIdSearch = !searchDoterraId || 
      user.doterraId?.includes(searchDoterraId) ||
      user.mentorDoterraId?.includes(searchDoterraId);

    const matchesFilter = 
      filterBy === 'all' ||
      (filterBy === 'hasDoterraAccount' && user.hasDoterraAccount === 'yes') ||
      (filterBy === 'noDoterraAccount' && user.hasDoterraAccount === 'no') ||
      (filterBy === 'specialMentor' && user.mentorDoterraId === '13634849') ||
      (filterBy === 'active' && user.status === 'active') ||
      (filterBy === 'inactive' && user.status === 'inactive') ||
      (filterBy === 'hasPhone' && user.phoneNumber && user.phoneNumber.trim() !== '') ||
      (filterBy === 'noPhone' && (!user.phoneNumber || user.phoneNumber.trim() === '')) ||
      (filterBy === 'recent' && user.createdAt && new Date(user.createdAt.seconds * 1000) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) ||
      (filterBy === 'termsAccepted' && user.acceptTerms === true) ||
      (filterBy === 'termsNotAccepted' && user.acceptTerms !== true);

    return matchesSearch && matchesEmailSearch && matchesDoterraIdSearch && matchesFilter;
  }).sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'createdAt') {
      aValue = new Date(a.createdAt?.seconds * 1000 || 0);
      bValue = new Date(b.createdAt?.seconds * 1000 || 0);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const rowsPerPage = tableSettings.rowsPerPage;
  const indexOfLastUser = currentPage * rowsPerPage;
  const indexOfFirstUser = indexOfLastUser - rowsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  // Enhanced Selection functions for mobile and desktop
  const [longPressTimer, setLongPressTimer] = useState(null);

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.docId));
    }
  };

  const handleLongPressStart = (userId) => {
    if (window.innerWidth <= 768) {
      const timer = setTimeout(() => {
        if (!selectMode) {
          setSelectMode(true);
          setSelectedUsers([userId]);
          // Vibration feedback if supported
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, 500);
      setLongPressTimer(timer);
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleDesktopSelect = (userId, event) => {
    // Desktop selection with Ctrl/Cmd key for multiple selection
    if (event.ctrlKey || event.metaKey) {
      if (!selectMode) {
        setSelectMode(true);
      }
      handleSelectUser(userId);
    } else if (event.shiftKey && selectMode && selectedUsers.length > 0) {
      // Shift-click for range selection
      const userIds = filteredUsers.map(user => user.docId);
      const lastSelectedIndex = userIds.indexOf(selectedUsers[selectedUsers.length - 1]);
      const currentIndex = userIds.indexOf(userId);
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      const rangeIds = userIds.slice(start, end + 1);
      
      setSelectedUsers(prev => {
        const newSelection = new Set([...prev, ...rangeIds]);
        return Array.from(newSelection);
      });
    } else if (selectMode) {
      handleSelectUser(userId);
    }
  };

  const handleExitSelectMode = () => {
    setSelectMode(false);
    setSelectedUsers([]);
  };

  const handleQuickSelectAll = () => {
    if (!selectMode) {
      setSelectMode(true);
    }
    handleSelectAll();
  };

  if (loading) {
    return (
      <div className="users-table-loading">
        <div className="loading-spinner"></div>
        <p>Se încarcă utilizatorii...</p>
      </div>
    );
  }

  return (
    <div className="users-table-container">
      {/* Header Controls */}
      <div className="table-header" style={{
        display: 'flex',
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: '16px',
        alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
        justifyContent: 'space-between',
        padding: '16px 0',
        flexWrap: 'wrap'
      }}>
        <div className="header-left" style={{
          flex: window.innerWidth <= 768 ? '1' : '0 1 auto',
          minWidth: window.innerWidth <= 768 ? '100%' : '300px'
        }}>
          <div className="search-container" style={{
            position: 'relative',
            width: '100%',
            maxWidth: window.innerWidth <= 768 ? '100%' : '400px'
          }}>
            <Search size={20} className="search-icon" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d',
              zIndex: 1
            }} />
            <input
              type="text"
              placeholder={t('users.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#ffffff',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#904d71'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
        </div>
        
        <div className="header-right" style={{
          display: 'flex',
          gap: window.innerWidth <= 768 ? '8px' : '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-end'
        }}>
          <button 
            className={`btn btn-glass ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
              borderRadius: '10px',
              border: 'none',
              background: showFilters ? 'rgba(109, 44, 145, 0.1)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              color: showFilters ? '#6d2c91' : '#495057',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px'
            }}
          >
            <Filter size={window.innerWidth <= 768 ? 14 : 16} />
            <span className={window.innerWidth <= 768 ? 'btn-text-mobile' : 'btn-text'}>
              {window.innerWidth <= 768 ? 'Filtre' : 'Filtre avansate'}
            </span>
            <ChevronDown size={window.innerWidth <= 768 ? 14 : 16} className={`chevron ${showFilters ? 'rotated' : ''}`} />
          </button>
          
          <button 
            className="btn btn-glass btn-export" 
            onClick={handleExport}
            style={{
              padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
              borderRadius: '10px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              color: '#495057',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px'
            }}
          >
            <Download size={window.innerWidth <= 768 ? 14 : 16} />
            <span className={window.innerWidth <= 768 ? 'btn-text-mobile' : 'btn-text'}>
              Exportă
            </span>
          </button>
          
          <button 
            className="btn btn-glass" 
            onClick={() => setShowConfigModal(true)}
            style={{
              padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
              borderRadius: '10px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              color: '#495057',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px'
            }}
          >
            <Settings size={window.innerWidth <= 768 ? 14 : 16} />
            <span className={window.innerWidth <= 768 ? 'btn-text-mobile' : 'btn-text'}>
              {window.innerWidth <= 768 ? 'Config' : 'Configurare'}
            </span>
          </button>
          
          <div className="dropdown" style={{position: 'relative'}}>
            <button 
              className="btn btn-glass dropdown-toggle"
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              style={{
                padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                borderRadius: '10px',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                color: '#495057',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: window.innerWidth <= 768 ? '12px' : '14px'
              }}
            >
              <MoreVertical size={window.innerWidth <= 768 ? 14 : 16} />
              <span className={window.innerWidth <= 768 ? 'btn-text-mobile' : 'btn-text'}>
                Acțiuni
              </span>
              <ChevronDown size={window.innerWidth <= 768 ? 14 : 16} />
            </button>
            <div className={`dropdown-menu actions-dropdown-modern ${showActionsDropdown ? 'show' : ''}`}>
              <div className="dropdown-header">
                <span>Acțiuni generale</span>
              </div>
              <button onClick={fetchUsers} className="dropdown-item">
                <Download size={16} />
                Reîncarcă date
              </button>
              
              {selectedUsers.length > 0 && (
                <>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-header">
                    <span>Acțiuni pentru {selectedUsers.length} utilizatori</span>
                  </div>
                  <button 
                    onClick={() => handleBulkAction('activate')} 
                    className="dropdown-item"
                  >
                    <UserCheck size={16} />
                    Activează utilizatorii selectați
                  </button>
                  <button 
                    onClick={() => handleBulkAction('deactivate')} 
                    className="dropdown-item"
                  >
                    <UserX size={16} />
                    Dezactivează utilizatorii selectați
                  </button>
                  <button 
                    onClick={() => handleBulkAction('sendEmail')} 
                    className="dropdown-item"
                  >
                    <Mail size={16} />
                    Trimite email în masă
                  </button>
                  <button 
                    onClick={() => handleBulkAction('export')} 
                    className="dropdown-item"
                  >
                    <Download size={16} />
                    Exportă utilizatorii selectați
                  </button>
                  <button 
                    onClick={() => handleBulkAction('delete')} 
                    className="dropdown-item danger"
                  >
                    <Trash2 size={16} />
                    Șterge utilizatorii selectați
                  </button>
                </>
              )}
              
              <div className="dropdown-divider"></div>
              <button 
                onClick={handleDeleteAll} 
                className="dropdown-item danger"
              >
                <Trash2 size={16} />
                Șterge toți utilizatorii
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label>Filtrează după:</label>
              <select 
                value={filterBy} 
                onChange={(e) => setFilterBy(e.target.value)}
                className="filter-select"
              >
                <option value="all">Toți utilizatorii</option>
                <option value="hasDoterraAccount">Cu cont doTERRA</option>
                <option value="noDoterraAccount">Fără cont doTERRA</option>
                <option value="specialMentor">Mentor special (13634849)</option>
                <option value="active">Utilizatori activi</option>
                <option value="inactive">Utilizatori inactivi</option>
                <option value="hasPhone">Cu număr de telefon</option>
                <option value="noPhone">Fără număr de telefon</option>
                <option value="recent">Înregistrați recent (ultimele 30 zile)</option>
                <option value="termsAccepted">Termeni acceptați</option>
                <option value="termsNotAccepted">Termeni neacceptați</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Sortează după:</label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="id">ID</option>
                <option value="email">Email</option>
                <option value="firstName">Nume</option>
                <option value="lastName">Prenume</option>
                <option value="createdAt">Data creării</option>
                <option value="phoneNumber">Număr de telefon</option>
                <option value="doterraId">doTERRA ID</option>
                <option value="mentorDoterraId">Mentor doTERRA ID</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Ordine:</label>
              <select 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="filter-select"
              >
                <option value="asc">Crescător</option>
                <option value="desc">Descrescător</option>
              </select>
            </div>
          </div>
          
          <div className="filter-row">
            <div className="filter-group">
              <label>Caută în nume:</label>
              <input
                type="text"
                placeholder="Caută nume..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label>Caută în email:</label>
              <input
                type="text"
                placeholder="Caută email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label>doTERRA ID:</label>
              <input
                type="text"
                placeholder="Caută doTERRA ID..."
                value={searchDoterraId}
                onChange={(e) => setSearchDoterraId(e.target.value)}
                className="filter-input"
              />
            </div>
          </div>
          
          <div className="filter-actions">
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setSearchEmail('');
                setSearchDoterraId('');
                setFilterBy('all');
                setSortBy('id');
                setSortOrder('asc');
              }}
            >
              <X size={16} />
              Șterge toate filtrele
            </button>
            <span className="results-count">
              {filteredUsers.length} rezultate din {users.length} utilizatori
            </span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="table-stats">
        <div className="stat-item">
          <span className="stat-number">{users.length}</span>
          <span className="stat-label">Total utilizatori</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {users.filter(u => u.hasDoterraAccount === 'yes').length}
          </span>
          <span className="stat-label">Cu cont doTERRA</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {users.filter(u => u.mentorDoterraId === '13634849').length}
          </span>
          <span className="stat-label">Mentor special</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{filteredUsers.length}</span>
          <span className="stat-label">Rezultate filtrate</span>
        </div>
      </div>

      {/* Selection Controls Bar - positioned between stats and table */}
      <div className="selection-controls-bar" style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '12px',
        margin: '20px 0',
        padding: '12px 20px',
        background: selectMode ? 'rgba(144, 77, 113, 0.1)' : 'transparent',
        borderRadius: selectMode ? '12px' : '0',
        border: selectMode ? '1px solid rgba(144, 77, 113, 0.2)' : 'none',
        transition: 'all 0.3s ease'
      }}>
        {!selectMode ? (
          <>
            <button 
              className="btn btn-outline select-mode-btn"
              onClick={() => setSelectMode(true)}
              title="Activează modul selecție"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '2px solid #904d71',
                background: 'transparent',
                color: '#904d71',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '14px'
              }}
            >
              <div className="btn-content" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <UserCheck size={14} />
                <span>Selectează</span>
              </div>
            </button>
            <button 
              className="btn btn-outline-primary quick-select-all"
              onClick={handleQuickSelectAll}
              title="Selectează toți utilizatorii din listă"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                color: '#ffffff',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '14px'
              }}
            >
              <div className="btn-content" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <UserCheck size={14} />
                <span>Selectează tot</span>
              </div>
            </button>
          </>
        ) : (
          <>
            <button 
              className="btn btn-secondary" 
              onClick={handleExitSelectMode}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#6c757d',
                color: '#ffffff',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.3s ease'
              }}
            >
              <X size={16} />
              Ieși din selecție
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSelectAll}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                color: '#ffffff',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {selectedUsers.length === filteredUsers.length ? 'Deselectează tot' : 'Selectează tot'}
            </button>
            <span className="selected-count" style={{
              padding: '10px 16px',
              background: 'rgba(144, 77, 113, 0.1)',
              borderRadius: '8px',
              color: '#904d71',
              fontWeight: 600,
              fontSize: '14px'
            }}>
              {selectedUsers.length} utilizatori selectați
            </span>
          </>
        )}
      </div>

      {/* Table */}
      <div className={`table-wrapper ${tableSettings.compactView ? 'compact-view' : ''}`}>
        <table className="users-table">
          <thead>
            <tr>
              {(selectMode || selectedUsers.length > 0) && (
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                    className="select-all-checkbox"
                  />
                </th>
              )}
              {visibleColumns.id && <th>ID</th>}
              {visibleColumns.email && <th>Email</th>}
              {visibleColumns.name && <th>Nume complet</th>}
              {visibleColumns.phone && <th>Telefon</th>}
              {visibleColumns.doterraId && <th>doTERRA ID</th>}
              {visibleColumns.mentor && <th>Mentor doTERRA</th>}
              {visibleColumns.status && <th>Status cont</th>}
              {visibleColumns.createdAt && <th>Data creării</th>}
              {visibleColumns.actions && <th>Acțiuni</th>}
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr 
                key={user.docId} 
                className={`table-row ${selectedUsers.includes(user.docId) ? 'selected' : ''}`}
                onTouchStart={() => handleLongPressStart(user.docId)}
                onTouchEnd={handleLongPressEnd}
                onTouchCancel={handleLongPressEnd}
                onClick={(event) => {
                  if (selectMode) {
                    handleSelectUser(user.docId);
                  } else if (window.innerWidth > 768) {
                    // Desktop: Enable selection with Ctrl/Cmd click
                    handleDesktopSelect(user.docId, event);
                  }
                }}
              >
                {(selectMode || selectedUsers.length > 0) && (
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.docId)}
                      onChange={() => handleSelectUser(user.docId)}
                      className="user-checkbox"
                    />
                  </td>
                )}
                {visibleColumns.id && (
                  <td className="id-cell">{user.id}</td>
                )}
                {visibleColumns.email && (
                  <td className="email-cell">
                    <div className="user-email">
                      <Mail size={14} />
                      {user.email}
                    </div>
                  </td>
                )}
                {visibleColumns.name && (
                  <td className="name-cell">
                    <div className="user-name">
                      <strong>{user.firstName} {user.lastName}</strong>
                      {user.doterraFullName && user.doterraFullName !== `${user.firstName} ${user.lastName}` && (
                        <small>doTERRA: {user.doterraFullName}</small>
                      )}
                    </div>
                  </td>
                )}
                {visibleColumns.phone && (
                  <td className="phone-cell">
                    {user.phoneNumber && (
                      <div className="user-phone">
                        <Phone size={14} />
                        {user.phoneNumber}
                      </div>
                    )}
                  </td>
                )}
                {visibleColumns.doterraId && (
                  <td className="doterra-id-cell">
                    {user.doterraId && (
                      <span className="doterra-id">{user.doterraId}</span>
                    )}
                  </td>
                )}
                {visibleColumns.mentor && (
                  <td className="mentor-cell">
                    {user.mentorDoterraId && (
                      <div className="mentor-info">
                        <span className={`mentor-id ${user.mentorDoterraId === '13634849' ? 'special' : ''}`}>
                          {user.mentorDoterraId}
                          {user.mentorDoterraId === '13634849' && (
                            <span className="special-badge">Special</span>
                          )}
                        </span>
                        {user.mentorFullName && (
                          <small>{user.mentorFullName}</small>
                        )}
                      </div>
                    )}
                  </td>
                )}
                {visibleColumns.status && (
                  <td className="status-cell">
                    <div className="status-badges">
                      {user.hasDoterraAccount === 'yes' ? (
                        <span className="badge success">
                          <UserCheck size={12} />
                          Cu cont doTERRA
                        </span>
                      ) : (
                        <span className="badge warning">
                          <UserX size={12} />
                          Fără cont doTERRA
                        </span>
                      )}
                      {user.mentorDoterraId === '13634849' && (
                        <span className="badge special-team">
                          <Shield size={12} />
                          Membru echipă
                        </span>
                      )}
                    </div>
                  </td>
                )}
                {visibleColumns.createdAt && (
                  <td className="date-cell">
                    <div className="date-info">
                      <Calendar size={14} />
                      {(() => {
                        // Handle different date formats from Firebase
                        if (user.createdAt) {
                          if (user.createdAt.seconds) {
                            // Firestore Timestamp format
                            return new Date(user.createdAt.seconds * 1000).toLocaleDateString('ro-RO');
                          } else if (user.createdAt._seconds) {
                            // Alternative Firestore format
                            return new Date(user.createdAt._seconds * 1000).toLocaleDateString('ro-RO');
                          } else if (typeof user.createdAt === 'string') {
                            // ISO string format
                            return new Date(user.createdAt).toLocaleDateString('ro-RO');
                          } else if (user.createdAt instanceof Date) {
                            // Regular Date object
                            return user.createdAt.toLocaleDateString('ro-RO');
                          }
                        }
                        // Check other possible field names
                        if (user.created_at) {
                          return new Date(user.created_at).toLocaleDateString('ro-RO');
                        }
                        if (user.dateCreated) {
                          return new Date(user.dateCreated).toLocaleDateString('ro-RO');
                        }
                        if (user.registrationDate) {
                          return new Date(user.registrationDate).toLocaleDateString('ro-RO');
                        }
                        return 'N/A';
                      })()}
                    </div>
                  </td>
                )}
                {visibleColumns.actions && (
                  <td className="actions-cell">
                    <div className="action-buttons-glass">
                      <button 
                        className="btn-glass view"
                        title="Vizualizează detalii"
                        onClick={() => handleViewUser(user)}
                      >
                        <Eye size={14} />
                      </button>
                      <button 
                        className="btn-glass edit"
                        title="Editează utilizator"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        className="btn-glass email"
                        title="Trimite email"
                        onClick={() => handleSendEmail(user.email)}
                      >
                        <Mail size={14} />
                      </button>
                      {user.phoneNumber && (
                        <button 
                          className="btn-glass phone"
                          title="Apelează utilizator"
                          onClick={() => handleCallUser(user.phoneNumber)}
                        >
                          <Phone size={14} />
                        </button>
                      )}
                      <button 
                        className={`btn-glass ${user.status === 'active' ? 'deactivate' : 'activate'}`}
                        title={user.status === 'active' ? 'Dezactivează utilizator' : 'Activează utilizator'}
                        onClick={() => handleToggleUserStatus(user.docId, user.status)}
                      >
                        {user.status === 'active' ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                      <button 
                        className="btn-glass delete"
                        title="Șterge utilizator"
                        onClick={() => handleDeleteUser(user.docId)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Anterior
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`page-number ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button 
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Următorul
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="empty-state">
          <UserX size={48} className="empty-icon" />
          <h3>Nu au fost găsiți utilizatori</h3>
          <p>Încercați să modificați criteriile de căutare sau filtrele.</p>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Configurări Tabel Utilizatori</h3>
              <button className="modal-close" onClick={() => setShowConfigModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-content">
              {/* Column Visibility Section */}
              <div className="config-section">
                <h4>Coloane Vizibile</h4>
                <div className="config-grid">
                  {Object.entries({
                    id: 'ID',
                    email: 'Email', 
                    name: 'Nume complet',
                    phone: 'Telefon',
                    doterraId: 'doTERRA ID',
                    mentor: 'Mentor doTERRA',
                    status: 'Status cont',
                    createdAt: 'Data creării',
                    actions: 'Acțiuni'
                  }).map(([key, label]) => (
                    <label key={key} className="config-checkbox">
                      <input
                        type="checkbox"
                        checked={visibleColumns[key]}
                        onChange={() => handleColumnVisibilityChange(key)}
                      />
                      <span className="checkmark"></span>
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Table Settings Section */}
              <div className="config-section">
                <h4>Setări Tabel</h4>
                <div className="config-row">
                  <label>Rânduri per pagină:</label>
                  <select
                    value={tableSettings.rowsPerPage}
                    onChange={(e) => handleTableSettingsChange('rowsPerPage', parseInt(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <label className="config-checkbox">
                  <input
                    type="checkbox"
                    checked={tableSettings.compactView}
                    onChange={(e) => handleTableSettingsChange('compactView', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Vizualizare compactă
                </label>

                <label className="config-checkbox">
                  <input
                    type="checkbox"
                    checked={tableSettings.showAdvancedInfo}
                    onChange={(e) => handleTableSettingsChange('showAdvancedInfo', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Informații avansate
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="reset-button" onClick={resetToDefaults}>
                Resetează la Implicit
              </button>
              <button className="save-button" onClick={() => setShowConfigModal(false)}>
                Salvează Configurația
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details/Edit Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay-modern" onClick={() => setShowUserModal(false)}>
          <div 
            className={`user-modal-modern ${modalMode === 'view' ? 'view-mode' : 'edit-mode'}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90vw',
              maxWidth: '700px',
              margin: '0 auto',
              padding: window.innerWidth > 767 ? '10px' : '5px',
              background: modalMode === 'view' 
                ? 'rgba(255, 255, 255, 0.85)' 
                : '#ffffff',
              backdropFilter: modalMode === 'view' ? 'blur(20px) saturate(180%)' : 'none',
              borderRadius: '20px',
              boxShadow: modalMode === 'view' 
                ? '0 8px 32px 0 rgba(31, 38, 135, 0.37), 0 0 0 1px rgba(255, 255, 255, 0.18)' 
                : '0 10px 30px rgba(0, 0, 0, 0.15)',
              border: modalMode === 'view' ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '400px',
              maxHeight: '85vh',
              overflow: 'hidden'
            }}
          >
            <div className="modal-header-modern" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              background: modalMode === 'edit' ? 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)' : 'transparent',
              margin: modalMode === 'edit' ? '-10px -10px 0 -10px' : '0',
              borderRadius: modalMode === 'edit' ? '20px 20px 0 0' : '0'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                {modalMode === 'view' ? <Eye size={24} color="#904d71" /> : <Edit size={24} color="#ffffff" />}
                <h3 style={{
                  margin: 0,
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  color: modalMode === 'edit' ? '#ffffff' : '#904d71',
                  letterSpacing: '-0.5px'
                }}>
                  {modalMode === 'view' ? 'Detalii Utilizator' : 'Editează Utilizator'}
                </h3>
              </div>
              <button 
                className="modal-close-modern" 
                onClick={() => setShowUserModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '6px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.target.style.background = modalMode === 'edit' ? 'rgba(255,255,255,0.2)' : 'rgba(144,77,113,0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <X size={22} color={modalMode === 'edit' ? '#ffffff' : '#904d71'} />
              </button>
            </div>
            
            <div className="modal-content-modern" style={{
              padding: '24px',
              flex: 1,
              overflowY: 'auto',
              maxHeight: 'calc(85vh - 140px)'
            }}>
              <div className="user-details-modern">
                {modalMode === 'view' ? (
                  <>
                    <div className="detail-group-modern" style={{marginBottom: '24px'}}>
                      <h4 style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: '#904d71'
                      }}>
                        <User size={18} /> Informații Personale
                      </h4>
                      <div className="detail-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px'
                      }}>
                        <div className="detail-item">
                          <span className="detail-label">ID:</span>
                          <span className="detail-value">{selectedUser.id}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{selectedUser.email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Nume complet:</span>
                          <span className="detail-value">{selectedUser.firstName} {selectedUser.lastName}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Telefon:</span>
                          <span className="detail-value">{selectedUser.phoneNumber || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {selectedUser.hasDoterraAccount === 'yes' && (
                      <div className="detail-group-modern" style={{marginBottom: '24px'}}>
                        <h4 style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '16px',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          color: '#904d71'
                        }}>
                          <Shield size={18} /> Informații doTERRA
                        </h4>
                        <div className="detail-grid" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '12px'
                        }}>
                          <div className="detail-item">
                            <span className="detail-label">Nume doTERRA:</span>
                            <span className="detail-value">{selectedUser.doterraFullName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">ID doTERRA:</span>
                            <span className="detail-value">{selectedUser.doterraId || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Email doTERRA:</span>
                            <span className="detail-value">{selectedUser.doterraEmail || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Telefon doTERRA:</span>
                            <span className="detail-value">{selectedUser.doterraPhone || 'N/A'}</span>
                          </div>
                          <div className="detail-item" style={{gridColumn: '1 / -1'}}>
                            <span className="detail-label">Mentor:</span>
                            <span className="detail-value">
                              {selectedUser.mentorFullName || 'N/A'}
                              {selectedUser.mentorDoterraId && ` (ID: ${selectedUser.mentorDoterraId})`}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="detail-group-modern">
                      <h4 style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: '#904d71'
                      }}>
                        <Key size={18} /> Informații Cont
                      </h4>
                      <div className="detail-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px'
                      }}>
                        <div className="detail-item">
                          <span className="detail-label">Are cont doTERRA:</span>
                          <span className="detail-value">
                            {selectedUser.hasDoterraAccount === 'yes' ? 'Da' : 'Nu'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Termeni acceptați:</span>
                          <span className="detail-value">
                            {selectedUser.acceptTerms ? 'Da' : 'Nu'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Data creării:</span>
                          <span className="detail-value">
                            {selectedUser.createdAt && selectedUser.createdAt.seconds 
                              ? new Date(selectedUser.createdAt.seconds * 1000).toLocaleString('ro-RO')
                              : 'N/A'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Edit mode - Modern, professional form design
                  <div className="edit-form-modern">
                    <div className="form-content">
                      {/* Personal Information Section */}
                      <div className="form-section">
                        <div className="section-header">
                          <User size={16} />
                          <h3>Informații Personale</h3>
                        </div>
                        <div className="form-grid">
                          <div className="form-field">
                            <label htmlFor="firstName">Nume <span className="required">*</span></label>
                            <input
                              id="firstName"
                              type="text"
                              className="form-input"
                              placeholder="Introduceți numele"
                              value={editingUser?.firstName || ''}
                              onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                              required
                            />
                          </div>
                          <div className="form-field">
                            <label htmlFor="lastName">Prenume <span className="required">*</span></label>
                            <input
                              id="lastName"
                              type="text"
                              className="form-input"
                              placeholder="Introduceți prenumele"
                              value={editingUser?.lastName || ''}
                              onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-grid">
                          <div className="form-field">
                            <label htmlFor="email">Email <span className="required">*</span></label>
                            <div className="input-wrapper">
                              <Mail size={16} className="input-icon" />
                              <input
                                id="email"
                                type="email"
                                className="form-input with-icon"
                                placeholder="exemplu@email.com"
                                value={editingUser?.email || ''}
                                onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                required
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label htmlFor="phone">Telefon</label>
                            <div className="input-wrapper">
                              <Phone size={16} className="input-icon" />
                              <input
                                id="phone"
                                type="tel"
                                className="form-input with-icon"
                                placeholder="+40 xxx xxx xxx"
                                value={editingUser?.phoneNumber || ''}
                                onChange={(e) => setEditingUser({...editingUser, phoneNumber: e.target.value})}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Account Settings Section */}
                      <div className="form-section">
                        <div className="section-header">
                          <Settings size={16} />
                          <h3>Setări Cont</h3>
                        </div>
                        <div className="form-grid">
                          <div className="form-field">
                            <label htmlFor="hasDoterraAccount">Are cont doTERRA?</label>
                            <select
                              id="hasDoterraAccount"
                              className="form-select"
                              value={editingUser?.hasDoterraAccount || 'no'}
                              onChange={(e) => setEditingUser({...editingUser, hasDoterraAccount: e.target.value})}
                            >
                              <option value="no">Nu</option>
                              <option value="yes">Da</option>
                            </select>
                          </div>

                        </div>
                      </div>
                      
                      {/* Add Account Button for users without doTERRA account */}
                      {editingUser?.hasDoterraAccount === 'no' && !showDoterraForm && (
                        <div className="add-account-section">
                          <button 
                            type="button"
                            className="add-account-button"
                            onClick={() => setShowDoterraForm(true)}
                          >
                            <Shield size={16} />
                            <span>Adaugă cont</span>
                          </button>
                        </div>
                      )}
                      
                      {/* doTERRA Form for users without account when button is clicked */}
                      {(editingUser?.hasDoterraAccount === 'yes' || showDoterraForm) && (
                        <div className="form-section doterra-section">
                          <div className="section-header">
                            <Shield size={16} />
                            <h3>Informații doTERRA</h3>
                          </div>
                          <div className="form-grid">
                            <div className="form-field">
                              <label htmlFor="doterraFullName">Nume complet doTERRA</label>
                              <input
                                id="doterraFullName"
                                type="text"
                                className="form-input"
                                placeholder="Numele complet din contul doTERRA"
                                value={editingUser?.doterraFullName || ''}
                                onChange={(e) => setEditingUser({...editingUser, doterraFullName: e.target.value})}
                              />
                            </div>
                            <div className="form-field">
                              <label htmlFor="doterraId">ID doTERRA</label>
                              <input
                                id="doterraId"
                                type="text"
                                className="form-input"
                                placeholder="ID-ul din contul doTERRA"
                                value={editingUser?.doterraId || ''}
                                onChange={(e) => setEditingUser({...editingUser, doterraId: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="form-grid">
                            <div className="form-field">
                              <label htmlFor="doterraEmail">Email doTERRA</label>
                              <div className="input-wrapper">
                                <Mail size={16} className="input-icon" />
                                <input
                                  id="doterraEmail"
                                  type="email"
                                  className="form-input with-icon"
                                  placeholder="email@doterra.com"
                                  value={editingUser?.doterraEmail || ''}
                                  onChange={(e) => setEditingUser({...editingUser, doterraEmail: e.target.value})}
                                />
                              </div>
                            </div>
                            <div className="form-field">
                              <label htmlFor="doterraPhone">Telefon doTERRA</label>
                              <div className="input-wrapper">
                                <Phone size={16} className="input-icon" />
                                <input
                                  id="doterraPhone"
                                  type="tel"
                                  className="form-input with-icon"
                                  placeholder="+40 xxx xxx xxx"
                                  value={editingUser?.doterraPhone || ''}
                                  onChange={(e) => setEditingUser({...editingUser, doterraPhone: e.target.value})}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Mentor Information */}
                          <div className="mentor-section">
                            <h4>Informații Mentor</h4>
                            <div className="form-grid">
                              <div className="form-field">
                                <label htmlFor="mentorFullName">Nume complet mentor</label>
                                <input
                                  id="mentorFullName"
                                  type="text"
                                  className="form-input"
                                  placeholder="Numele complet al mentorului"
                                  value={editingUser?.mentorFullName || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditingUser({...editingUser, mentorFullName: value});
                                    // Auto-complete for Ariana
                                    if (value.toLowerCase().includes('ariana') && value.toLowerCase().includes('henzel')) {
                                      setEditingUser(prev => ({
                                        ...prev, 
                                        mentorFullName: 'Ariana Henzel',
                                        mentorDoterraId: '13634849'
                                      }));
                                    }
                                  }}
                                  list="mentor-suggestions"
                                />
                                <datalist id="mentor-suggestions">
                                  <option value="Ariana Henzel" />
                                </datalist>
                              </div>
                              <div className="form-field">
                                <label htmlFor="mentorDoterraId">ID doTERRA mentor</label>
                                <input
                                  id="mentorDoterraId"
                                  type="text"
                                  className="form-input"
                                  placeholder="ID-ul doTERRA al mentorului"
                                  value={editingUser?.mentorDoterraId || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditingUser({...editingUser, mentorDoterraId: value});
                                    // Auto-complete for Ariana's ID
                                    if (value === '13634849') {
                                      setEditingUser(prev => ({
                                        ...prev, 
                                        mentorFullName: 'Ariana Henzel',
                                        mentorDoterraId: '13634849'
                                      }));
                                    }
                                  }}
                                  list="mentor-id-suggestions"
                                />
                                <datalist id="mentor-id-suggestions">
                                  <option value="13634849" />
                                </datalist>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer-modern" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              gap: '16px'
            }}>
              {modalMode === 'view' ? (
                <>
                  <button 
                    className="action-button-glass secondary" 
                    onClick={() => setShowUserModal(false)}
                    style={{
                      padding: '12px 24px',
                      background: '#ffffff',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(144, 77, 113, 0.3)',
                      borderRadius: '12px',
                      color: '#904d71',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={16} />
                    Închide
                  </button>
                  <div className="action-group" style={{display: 'flex', gap: '12px'}}>
                    <button 
                      className="action-button-glass edit" 
                      onClick={() => {
                        setEditingUser({ ...selectedUser });
                        setModalMode('edit');
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'rgba(144, 77, 113, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '120px',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit size={16} />
                      Editează
                    </button>
                    <button 
                      className="action-button-glass danger"
                      onClick={() => {
                        setShowUserModal(false);
                        handleDeleteUser(selectedUser.docId);
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'rgba(220, 53, 69, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '120px',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                      Șterge
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button 
                    className="action-button secondary" 
                    onClick={() => {
                      setModalMode('view');
                      setEditingUser(null);
                    }}
                    style={{
                      padding: '12px 24px',
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '12px',
                      color: '#6c757d',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={16} />
                    Anulează
                  </button>
                  <button 
                    className="action-button primary" 
                    onClick={handleSaveUser}
                    disabled={savingUser}
                    style={{
                      padding: '12px 32px',
                      background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontWeight: 600,
                      cursor: savingUser ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '180px',
                      justifyContent: 'center',
                      opacity: savingUser ? 0.7 : 1
                    }}
                  >
                    {savingUser ? (
                      <div className="btn-content" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <div className="loading-spinner"></div>
                        <span>Se salvează...</span>
                      </div>
                    ) : (
                      <div className="btn-content" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <Save size={16} />
                        <span>Salvează modificările</span>
                      </div>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Export Modal */}
      <UserExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        users={users}
        filteredUsers={filteredUsers}
      />
      
      {/* Config Modal */}
      <UserConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
      />
    </div>
  );
};

export default UsersTable;
